// Core imports
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import { Card, Col, Row } from 'antd';
import CSATOverallScoreSection from './CSATOverallScoreSection';
import CSATSectionsScoreSection from './CSATSectionsScoreSection';
import CSATOvertimeScoreSection from './CSATOvertimeScoreSection';
import CSATOvertimeResponsesSection from './CSATOvertimeResponsesSection';

// Misc imports
import { ANALYTICS_SECTIONS } from '../../constants/main';
import { useFetchCSATAnalyticsQuery } from '../../store/analytics/analyticsApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';

const AnalyticsCSATTab = ({
  requestParams,
  setIsLoading = () => false,
  isPublicMode
}) => {

  const { data: csatAnalytics, isFetching: isFetchingCSATAnalytics, isUninitialized } = useFetchCSATAnalyticsQuery({ ...requestParams?.[0] }, { skip: isEmptyArray(requestParams) });

  // Memoize loading state to prevent unnecessary re-renders
  const isLoading = useMemo(() =>
    isFetchingCSATAnalytics || isUninitialized,
  [isFetchingCSATAnalytics, isUninitialized]
  );

  useEffect(() => {
    setIsLoading(isLoading);

    return (() => {
      setIsLoading(false);
    });
  }, [isLoading]);

  return (
    <Row gutter={[16, 16]} className="mb-5 hide-empty" id={`analytics-section_${ANALYTICS_SECTIONS.CSAT.key}`}>
      <Col xs={24} md={12} xxl={8} id={`analytics-section_${ANALYTICS_SECTIONS.CSAT.key}_${ANALYTICS_SECTIONS.CSAT.subSections.CSAT_OVERALL_SCORE.key}`}>
        <Card className="h-100">
          <CSATOverallScoreSection
            data={csatAnalytics?.csatOverallScore}
            sectionTitle={t('CSAT Overall Score')}
            isLoading={isLoading}
          />
        </Card>
      </Col>

      <Col xs={24} md={12} xxl={8} id={`analytics-section_${ANALYTICS_SECTIONS.CSAT.key}_${ANALYTICS_SECTIONS.CSAT.subSections.CSAT_SECTIONS_SCORE.key}`}>
        <Card className="h-100">
          <CSATSectionsScoreSection
            data={csatAnalytics?.csatSectionsScore}
            sectionTitle={t('CSAT Sections Score')}
            isLoading={isLoading}
          />
        </Card>
      </Col>

      <Col xs={24} md={12} xxl={8} id={`analytics-section_${ANALYTICS_SECTIONS.CSAT.key}_${ANALYTICS_SECTIONS.CSAT.subSections.CSAT_SCORE_OVERTIME.key}`}>
        <Card className="h-100">
          <CSATOvertimeScoreSection
            data={csatAnalytics?.csatOverTimeScore}
            sectionTitle={t('CSAT Score Overtime')}
            isLoading={false}
          />
        </Card>
      </Col>

      <Col xs={24} md={12} xxl={8} id={`analytics-section_${ANALYTICS_SECTIONS.CSAT.key}_${ANALYTICS_SECTIONS.CSAT.subSections.CSAT_RESPONSES_OVERTIME.key}`}>
        <Card className="h-100">
          <CSATOvertimeResponsesSection
            data={csatAnalytics?.csatOvertimeResponses}
            sectionTitle={t('CSAT Usage')}
            isLoading={false}
          />
        </Card>
      </Col>
    </Row>
  );
};

AnalyticsCSATTab.propTypes = {
  requestParams: PropTypes.array,
  setIsLoading: PropTypes.func,
  isPublicMode: PropTypes.bool
};

export default AnalyticsCSATTab;
